import {isString} from 'lodash';
import Token from 'markdown-it/lib/token';
import React, {FC, PropsWithChildren} from 'react';

import {MouseEventHandler} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {isCommonMarkGap} from '../../../../services/markdown';

/*
 * Props.
 */

interface MarkdownTokenFormatterProps {
  token: Token;
  onLinkClick?: (link: string, shiftKey?: boolean) => void;
}

/*
 * Component.
 */

// Note: this component is made in a way that it shouldn't be aware of where it's being used, so refrain from using custom styling here.

export const MarkdownTokenFormatter: FC<MarkdownTokenFormatterProps> = (props) => {
  const {token, children} = props;
  switch (token.type) {
    case 'softbreak':
      return <br />;

    case 'strong_open':
      return <strong>{children}</strong>;

    case 'em_open':
      return <em>{children}</em>;

    case 's_open':
      return <s>{children}</s>;

    case 'code_inline':
      return <code>{children}</code>;

    case 'blockquote_open':
      return <blockquote>{children}</blockquote>;

    case 'ordered_list_open':
      return <ol start={parseStartAttribute(token.attrGet('start'))}>{children}</ol>;

    case 'bullet_list_open':
      return <ul>{children}</ul>;

    case 'list_item_open':
      return <li>{children}</li>;

    case 'paragraph_open':
      return <p>{children}</p>;

    case 'fence':
      return (
        <pre>
          <code>{children}</code>
        </pre>
      );

    case 'link_open':
      return renderLink(props);

    case 'image':
      return renderImage(token);

    case 'html_inline':
    case 'html_block':
      // As an extra precaution, make sure the children were rendered to a string and not markup.
      if (!isString(children) || isCommonMarkGap(children)) {
        return null;
      }

      return <>{children}</>;

    default:
      return <>{children}</>;
  }
};

function parseStartAttribute(value: string | null): number | undefined {
  if (!value) {
    return undefined;
  }

  const parsedValue = Number(value);
  if (!Number.isInteger(parsedValue)) {
    return undefined;
  }

  return parsedValue;
}

function renderLink(props: PropsWithChildren<MarkdownTokenFormatterProps>) {
  const {token, onLinkClick, children} = props;
  const href = token.attrGet('href') || '#';
  const onLinkClickWrapper: MouseEventHandler = (e) => {
    if (!onLinkClick) {
      return;
    }

    e.preventDefault();
    onLinkClick(href, e.shiftKey);
  };

  return (
    <a href={href} onClick={onLinkClickWrapper} target="_blank" rel="noopener noreferrer">
      {children}
    </a>
  );
}

function renderImage(token: Token) {
  const src = token.attrGet('src');
  if (!src) {
    return null;
  }

  // Given ![foo](url), markdown-it gives us `foo` as the content of the token.
  const alt = token.content || src || '';
  return <img className="front-markdown-image" src={src} alt={alt} />;
}
